import { shuffle as shuffleArray } from "../utils/array";
import { isBlank } from "./text";
export function clone(puzzle) {
    const feats = new Array(puzzle.feats.length);
    for (let i = 0; i < puzzle.feats.length; i += 1) {
        feats[i] = [...puzzle.feats[i]];
    }
    return {
        featNames: [...puzzle.featNames],
        feats: feats,
        consts: [...puzzle.consts]
    };
}
export function purgeConstraints(p) {
    const featSet = new Set();
    p.feats.flat().forEach((feat) => {
        featSet.add(feat);
    });
    return p.consts.filter((c) => featSet.has(c.f1) && featSet.has(c.f2));
}
export function validate(p) {
    if (p.featNames.length < 2) {
        return "Need at least 2 feature types.";
    }
    if (p.featNames.length !== p.feats.length) {
        return "Inconsistent number of feature types and features.";
    }
    if (new Set(p.featNames).size != p.featNames.length) {
        return "Feature types must be unique.";
    }
    for (let featName of p.featNames) {
        if (isBlank(featName)) {
            return "Feature types must not be blank.";
        }
    }
    const persons = p.feats[0].length;
    if (persons < 2) {
        return "Need at least 2 persons.";
    }
    const featSet = new Set();
    for (let i = 0; i < p.feats.length; i += 1) {
        const featRow = p.feats[i];
        if (featRow.length !== persons) {
            return "Each feature type must have same number of features as persons.";
        }
        for (let j = 0; j < featRow.length; j += 1) {
            if (featSet.has(featRow[j])) {
                return "Features must be unique.";
            }
            if (isBlank(featRow[j])) {
                return "Features must not be blank.";
            }
            featSet.add(featRow[j]);
        }
    }
    for (let constraint of p.consts) {
        if (isBlank(constraint.f1) || isBlank(constraint.f2)) {
            return "Constraints must not be blank.";
        }
        if (!featSet.has(constraint.f1) || !featSet.has(constraint.f2)) {
            return "Constraint on unknown feature.";
        }
    }
    return null;
}
export function shuffle(puzzle) {
    puzzle.feats.forEach((featRow) => {
        shuffleArray(featRow);
    });
}
export const key = (c) => `${c.f1}-${c.f2}-${c.positive}`;
export function liftConstraints(puzzle, satisfied) {
    const featNameLookup = new Map();
    puzzle.feats.forEach((featRow, i) => featRow.forEach((feat) => featNameLookup.set(feat, puzzle.featNames[i])));
    const satisfiedLookup = new Set(satisfied.map(key));
    return puzzle.consts.map(c => (Object.assign(Object.assign({}, c), { key: key(c), satisfied: satisfiedLookup.has(key(c)), f1Name: featNameLookup.get(c.f1), f2Name: featNameLookup.get(c.f2), selected: false })));
}
export function liftPuzzle(lesser) {
    const featCount = lesser.feats.length;
    const peopleCount = lesser.feats[0].length;
    let featNames = featureNames();
    shuffleArray(featNames);
    featNames = featNames.slice(0, featCount);
    const featRowLookup = features();
    const feats = [];
    for (const featName of featNames) {
        const featRow = featRowLookup.get(featName);
        shuffleArray(featRow);
        feats.push(featRow.slice(0, peopleCount));
    }
    const featsLookup = new Map();
    lesser.feats.forEach((featRow, i) => {
        featRow.forEach((feat, j) => {
            featsLookup.set(feat, [i, j]);
        });
    });
    const consts = [];
    for (const constraint of lesser.consts) {
        const [i1, j1] = featsLookup.get(constraint.f1);
        const [i2, j2] = featsLookup.get(constraint.f2);
        consts.push({ f1: feats[i1][j1], f2: feats[i2][j2], positive: constraint.positive });
    }
    return {
        featNames,
        feats,
        consts
    };
}
export function getConstraintSummary(constraints) {
    const summary = { positive: new Set(), negative: new Set() };
    constraints.forEach((c) => {
        if (c.positive) {
            summary.positive.add(c.f1);
            summary.positive.add(c.f2);
        }
        else {
            summary.negative.add(c.f1);
            summary.negative.add(c.f2);
        }
    });
    return summary;
}
//////// Empty Puzzle 
export const EMPTY_PUZZLE = {
    featNames: ["", ""],
    feats: [["", ""], ["", ""]],
    consts: [],
};
//////// Example Puzzle 
const EXAMPLE_FEAT_NAMES = ["Nationality", "Pet", "Sport"];
const EXAMPLE_FEATS = [
    ["RU", "GB", "DE"],
    ["Rabbit", "Mouse", "Lion"],
    ["Volleyball", "Ballet", "Basketball"],
];
const EXAMPLE_CONSTRAINTS = [
    { f1: "GB", f2: "Mouse", positive: true },
    { f1: "DE", f2: "Ballet", positive: false },
    { f1: "Rabbit", f2: "Volleyball", positive: true },
    { f1: "Mouse", f2: "Basketball", positive: false },
    { f1: "Lion", f2: "Ballet", positive: false },
    { f1: "DE", f2: "Basketball", positive: false },
];
export const EXAMPLE_PUZZLE = {
    featNames: EXAMPLE_FEAT_NAMES,
    feats: EXAMPLE_FEATS,
    consts: EXAMPLE_CONSTRAINTS
};
//////// Medium Puzzle 
const MEDIUM_FEAT_NAMES = ["Feat0", "Feat1", "Feat2", "Feat3", "Feat4", "Feat5"];
const MEDIUM_FEATS = [
    ["02_AF", "02_AC", "02_AE", "02_AB", "02_AD", "02_AA"],
    ["00_AC", "00_AD", "00_AF", "00_AA", "00_AB", "00_AE"],
    ["05_AA", "05_AD", "05_AE", "05_AB", "05_AC", "05_AF"],
    ["03_AD", "03_AA", "03_AF", "03_AB", "03_AE", "03_AC"],
    ["04_AF", "04_AC", "04_AE", "04_AB", "04_AD", "04_AA"],
    ["01_AF", "01_AB", "01_AD", "01_AA", "01_AE", "01_AC"]
];
const MEDIUM_CONSTRAINTS = [
    { f1: "00_AA", f2: "01_AA", positive: true },
    { f1: "00_AB", f2: "01_AB", positive: true },
    { f1: "00_AC", f2: "01_AC", positive: true },
    { f1: "00_AD", f2: "01_AE", positive: false },
    { f1: "00_AE", f2: "01_AF", positive: false },
    { f1: "00_AF", f2: "01_AD", positive: false },
    { f1: "00_AD", f2: "01_AF", positive: false },
    { f1: "00_AE", f2: "01_AD", positive: false },
    { f1: "00_AF", f2: "01_AE", positive: false },
    { f1: "01_AA", f2: "02_AA", positive: true },
    { f1: "01_AB", f2: "02_AB", positive: true },
    { f1: "01_AC", f2: "02_AC", positive: true },
    { f1: "01_AD", f2: "02_AE", positive: false },
    { f1: "01_AE", f2: "02_AF", positive: false },
    { f1: "01_AF", f2: "02_AD", positive: false },
    { f1: "01_AD", f2: "02_AF", positive: false },
    { f1: "01_AE", f2: "02_AD", positive: false },
    { f1: "01_AF", f2: "02_AE", positive: false },
    { f1: "02_AA", f2: "03_AA", positive: true },
    { f1: "02_AB", f2: "03_AB", positive: true },
    { f1: "02_AC", f2: "03_AC", positive: true },
    { f1: "02_AD", f2: "03_AE", positive: false },
    { f1: "02_AE", f2: "03_AF", positive: false },
    { f1: "02_AF", f2: "03_AD", positive: false },
    { f1: "02_AD", f2: "03_AF", positive: false },
    { f1: "02_AE", f2: "03_AD", positive: false },
    { f1: "02_AF", f2: "03_AE", positive: false },
    { f1: "03_AA", f2: "04_AA", positive: true },
    { f1: "03_AB", f2: "04_AB", positive: true },
    { f1: "03_AC", f2: "04_AC", positive: true },
    { f1: "03_AD", f2: "04_AE", positive: false },
    { f1: "03_AE", f2: "04_AF", positive: false },
    { f1: "03_AF", f2: "04_AD", positive: false },
    { f1: "03_AD", f2: "04_AF", positive: false },
    { f1: "03_AE", f2: "04_AD", positive: false },
    { f1: "03_AF", f2: "04_AE", positive: false },
    { f1: "04_AA", f2: "05_AA", positive: true },
    { f1: "04_AB", f2: "05_AB", positive: true },
    { f1: "04_AC", f2: "05_AC", positive: true },
    { f1: "04_AD", f2: "05_AE", positive: false },
    { f1: "04_AE", f2: "05_AF", positive: false },
    { f1: "04_AF", f2: "05_AD", positive: false },
    { f1: "04_AD", f2: "05_AF", positive: false },
    { f1: "04_AE", f2: "05_AD", positive: false },
    { f1: "04_AF", f2: "05_AE", positive: false },
];
export const MEDIUM_PUZZLE = {
    featNames: MEDIUM_FEAT_NAMES,
    feats: MEDIUM_FEATS,
    consts: MEDIUM_CONSTRAINTS
};
//////// Large Puzzle 
const LARGE_FEAT_NAMES = ["Feat0", "Feat1", "Feat2", "Feat3", "Feat4", "Feat5", "Feat6", "Feat7", "Feat8", "Feat9"];
const LARGE_FEATS = [
    ["00_AJ", "00_AD", "00_AE", "00_AG", "00_AB", "00_AH", "00_AI", "00_AC", "00_AA", "00_AF"],
    ["01_AB", "01_AH", "01_AJ", "01_AC", "01_AG", "01_AE", "01_AI", "01_AA", "01_AD", "01_AF"],
    ["03_AB", "03_AI", "03_AH", "03_AG", "03_AJ", "03_AF", "03_AC", "03_AD", "03_AE", "03_AA"],
    ["06_AG", "06_AF", "06_AE", "06_AD", "06_AC", "06_AA", "06_AJ", "06_AH", "06_AI", "06_AB"],
    ["05_AF", "05_AA", "05_AH", "05_AC", "05_AJ", "05_AG", "05_AB", "05_AI", "05_AE", "05_AD"],
    ["02_AC", "02_AF", "02_AG", "02_AE", "02_AA", "02_AI", "02_AD", "02_AJ", "02_AH", "02_AB"],
    ["09_AE", "09_AI", "09_AC", "09_AG", "09_AJ", "09_AA", "09_AF", "09_AD", "09_AH", "09_AB"],
    ["07_AF", "07_AG", "07_AC", "07_AJ", "07_AE", "07_AD", "07_AA", "07_AH", "07_AI", "07_AB"],
    ["08_AJ", "08_AE", "08_AI", "08_AF", "08_AG", "08_AC", "08_AA", "08_AB", "08_AH", "08_AD"],
    ["04_AD", "04_AI", "04_AB", "04_AH", "04_AF", "04_AJ", "04_AA", "04_AC", "04_AG", "04_AE"]
];
const LARGE_CONSTRAINTS = [
    { f1: "00_AA", f2: "01_AA", positive: true },
    { f1: "00_AB", f2: "01_AB", positive: true },
    { f1: "00_AC", f2: "01_AC", positive: true },
    { f1: "00_AD", f2: "01_AD", positive: true },
    { f1: "00_AE", f2: "01_AE", positive: true },
    { f1: "00_AF", f2: "01_AG", positive: false },
    { f1: "00_AG", f2: "01_AH", positive: false },
    { f1: "00_AH", f2: "01_AI", positive: false },
    { f1: "00_AI", f2: "01_AJ", positive: false },
    { f1: "00_AJ", f2: "01_AF", positive: false },
    { f1: "00_AF", f2: "01_AH", positive: false },
    { f1: "00_AG", f2: "01_AI", positive: false },
    { f1: "00_AH", f2: "01_AJ", positive: false },
    { f1: "00_AI", f2: "01_AF", positive: false },
    { f1: "00_AJ", f2: "01_AG", positive: false },
    { f1: "00_AF", f2: "01_AI", positive: false },
    { f1: "00_AG", f2: "01_AJ", positive: false },
    { f1: "00_AH", f2: "01_AF", positive: false },
    { f1: "00_AI", f2: "01_AG", positive: false },
    { f1: "00_AJ", f2: "01_AH", positive: false },
    { f1: "00_AF", f2: "01_AJ", positive: false },
    { f1: "00_AG", f2: "01_AF", positive: false },
    { f1: "00_AH", f2: "01_AG", positive: false },
    { f1: "00_AI", f2: "01_AH", positive: false },
    { f1: "00_AJ", f2: "01_AI", positive: false },
    { f1: "01_AA", f2: "02_AA", positive: true },
    { f1: "01_AB", f2: "02_AB", positive: true },
    { f1: "01_AC", f2: "02_AC", positive: true },
    { f1: "01_AD", f2: "02_AD", positive: true },
    { f1: "01_AE", f2: "02_AE", positive: true },
    { f1: "01_AF", f2: "02_AG", positive: false },
    { f1: "01_AG", f2: "02_AH", positive: false },
    { f1: "01_AH", f2: "02_AI", positive: false },
    { f1: "01_AI", f2: "02_AJ", positive: false },
    { f1: "01_AJ", f2: "02_AF", positive: false },
    { f1: "01_AF", f2: "02_AH", positive: false },
    { f1: "01_AG", f2: "02_AI", positive: false },
    { f1: "01_AH", f2: "02_AJ", positive: false },
    { f1: "01_AI", f2: "02_AF", positive: false },
    { f1: "01_AJ", f2: "02_AG", positive: false },
    { f1: "01_AF", f2: "02_AI", positive: false },
    { f1: "01_AG", f2: "02_AJ", positive: false },
    { f1: "01_AH", f2: "02_AF", positive: false },
    { f1: "01_AI", f2: "02_AG", positive: false },
    { f1: "01_AJ", f2: "02_AH", positive: false },
    { f1: "01_AF", f2: "02_AJ", positive: false },
    { f1: "01_AG", f2: "02_AF", positive: false },
    { f1: "01_AH", f2: "02_AG", positive: false },
    { f1: "01_AI", f2: "02_AH", positive: false },
    { f1: "01_AJ", f2: "02_AI", positive: false },
    { f1: "02_AA", f2: "03_AA", positive: true },
    { f1: "02_AB", f2: "03_AB", positive: true },
    { f1: "02_AC", f2: "03_AC", positive: true },
    { f1: "02_AD", f2: "03_AD", positive: true },
    { f1: "02_AE", f2: "03_AE", positive: true },
    { f1: "02_AF", f2: "03_AG", positive: false },
    { f1: "02_AG", f2: "03_AH", positive: false },
    { f1: "02_AH", f2: "03_AI", positive: false },
    { f1: "02_AI", f2: "03_AJ", positive: false },
    { f1: "02_AJ", f2: "03_AF", positive: false },
    { f1: "02_AF", f2: "03_AH", positive: false },
    { f1: "02_AG", f2: "03_AI", positive: false },
    { f1: "02_AH", f2: "03_AJ", positive: false },
    { f1: "02_AI", f2: "03_AF", positive: false },
    { f1: "02_AJ", f2: "03_AG", positive: false },
    { f1: "02_AF", f2: "03_AI", positive: false },
    { f1: "02_AG", f2: "03_AJ", positive: false },
    { f1: "02_AH", f2: "03_AF", positive: false },
    { f1: "02_AI", f2: "03_AG", positive: false },
    { f1: "02_AJ", f2: "03_AH", positive: false },
    { f1: "02_AF", f2: "03_AJ", positive: false },
    { f1: "02_AG", f2: "03_AF", positive: false },
    { f1: "02_AH", f2: "03_AG", positive: false },
    { f1: "02_AI", f2: "03_AH", positive: false },
    { f1: "02_AJ", f2: "03_AI", positive: false },
    { f1: "03_AA", f2: "04_AA", positive: true },
    { f1: "03_AB", f2: "04_AB", positive: true },
    { f1: "03_AC", f2: "04_AC", positive: true },
    { f1: "03_AD", f2: "04_AD", positive: true },
    { f1: "03_AE", f2: "04_AE", positive: true },
    { f1: "03_AF", f2: "04_AG", positive: false },
    { f1: "03_AG", f2: "04_AH", positive: false },
    { f1: "03_AH", f2: "04_AI", positive: false },
    { f1: "03_AI", f2: "04_AJ", positive: false },
    { f1: "03_AJ", f2: "04_AF", positive: false },
    { f1: "03_AF", f2: "04_AH", positive: false },
    { f1: "03_AG", f2: "04_AI", positive: false },
    { f1: "03_AH", f2: "04_AJ", positive: false },
    { f1: "03_AI", f2: "04_AF", positive: false },
    { f1: "03_AJ", f2: "04_AG", positive: false },
    { f1: "03_AF", f2: "04_AI", positive: false },
    { f1: "03_AG", f2: "04_AJ", positive: false },
    { f1: "03_AH", f2: "04_AF", positive: false },
    { f1: "03_AI", f2: "04_AG", positive: false },
    { f1: "03_AJ", f2: "04_AH", positive: false },
    { f1: "03_AF", f2: "04_AJ", positive: false },
    { f1: "03_AG", f2: "04_AF", positive: false },
    { f1: "03_AH", f2: "04_AG", positive: false },
    { f1: "03_AI", f2: "04_AH", positive: false },
    { f1: "03_AJ", f2: "04_AI", positive: false },
    { f1: "04_AA", f2: "05_AA", positive: true },
    { f1: "04_AB", f2: "05_AB", positive: true },
    { f1: "04_AC", f2: "05_AC", positive: true },
    { f1: "04_AD", f2: "05_AD", positive: true },
    { f1: "04_AE", f2: "05_AE", positive: true },
    { f1: "04_AF", f2: "05_AG", positive: false },
    { f1: "04_AG", f2: "05_AH", positive: false },
    { f1: "04_AH", f2: "05_AI", positive: false },
    { f1: "04_AI", f2: "05_AJ", positive: false },
    { f1: "04_AJ", f2: "05_AF", positive: false },
    { f1: "04_AF", f2: "05_AH", positive: false },
    { f1: "04_AG", f2: "05_AI", positive: false },
    { f1: "04_AH", f2: "05_AJ", positive: false },
    { f1: "04_AI", f2: "05_AF", positive: false },
    { f1: "04_AJ", f2: "05_AG", positive: false },
    { f1: "04_AF", f2: "05_AI", positive: false },
    { f1: "04_AG", f2: "05_AJ", positive: false },
    { f1: "04_AH", f2: "05_AF", positive: false },
    { f1: "04_AI", f2: "05_AG", positive: false },
    { f1: "04_AJ", f2: "05_AH", positive: false },
    { f1: "04_AF", f2: "05_AJ", positive: false },
    { f1: "04_AG", f2: "05_AF", positive: false },
    { f1: "04_AH", f2: "05_AG", positive: false },
    { f1: "04_AI", f2: "05_AH", positive: false },
    { f1: "04_AJ", f2: "05_AI", positive: false },
    { f1: "05_AA", f2: "06_AA", positive: true },
    { f1: "05_AB", f2: "06_AB", positive: true },
    { f1: "05_AC", f2: "06_AC", positive: true },
    { f1: "05_AD", f2: "06_AD", positive: true },
    { f1: "05_AE", f2: "06_AE", positive: true },
    { f1: "05_AF", f2: "06_AG", positive: false },
    { f1: "05_AG", f2: "06_AH", positive: false },
    { f1: "05_AH", f2: "06_AI", positive: false },
    { f1: "05_AI", f2: "06_AJ", positive: false },
    { f1: "05_AJ", f2: "06_AF", positive: false },
    { f1: "05_AF", f2: "06_AH", positive: false },
    { f1: "05_AG", f2: "06_AI", positive: false },
    { f1: "05_AH", f2: "06_AJ", positive: false },
    { f1: "05_AI", f2: "06_AF", positive: false },
    { f1: "05_AJ", f2: "06_AG", positive: false },
    { f1: "05_AF", f2: "06_AI", positive: false },
    { f1: "05_AG", f2: "06_AJ", positive: false },
    { f1: "05_AH", f2: "06_AF", positive: false },
    { f1: "05_AI", f2: "06_AG", positive: false },
    { f1: "05_AJ", f2: "06_AH", positive: false },
    { f1: "05_AF", f2: "06_AJ", positive: false },
    { f1: "05_AG", f2: "06_AF", positive: false },
    { f1: "05_AH", f2: "06_AG", positive: false },
    { f1: "05_AI", f2: "06_AH", positive: false },
    { f1: "05_AJ", f2: "06_AI", positive: false },
    { f1: "06_AA", f2: "07_AA", positive: true },
    { f1: "06_AB", f2: "07_AB", positive: true },
    { f1: "06_AC", f2: "07_AC", positive: true },
    { f1: "06_AD", f2: "07_AD", positive: true },
    { f1: "06_AE", f2: "07_AE", positive: true },
    { f1: "06_AF", f2: "07_AG", positive: false },
    { f1: "06_AG", f2: "07_AH", positive: false },
    { f1: "06_AH", f2: "07_AI", positive: false },
    { f1: "06_AI", f2: "07_AJ", positive: false },
    { f1: "06_AJ", f2: "07_AF", positive: false },
    { f1: "06_AF", f2: "07_AH", positive: false },
    { f1: "06_AG", f2: "07_AI", positive: false },
    { f1: "06_AH", f2: "07_AJ", positive: false },
    { f1: "06_AI", f2: "07_AF", positive: false },
    { f1: "06_AJ", f2: "07_AG", positive: false },
    { f1: "06_AF", f2: "07_AI", positive: false },
    { f1: "06_AG", f2: "07_AJ", positive: false },
    { f1: "06_AH", f2: "07_AF", positive: false },
    { f1: "06_AI", f2: "07_AG", positive: false },
    { f1: "06_AJ", f2: "07_AH", positive: false },
    { f1: "06_AF", f2: "07_AJ", positive: false },
    { f1: "06_AG", f2: "07_AF", positive: false },
    { f1: "06_AH", f2: "07_AG", positive: false },
    { f1: "06_AI", f2: "07_AH", positive: false },
    { f1: "06_AJ", f2: "07_AI", positive: false },
    { f1: "07_AA", f2: "08_AA", positive: true },
    { f1: "07_AB", f2: "08_AB", positive: true },
    { f1: "07_AC", f2: "08_AC", positive: true },
    { f1: "07_AD", f2: "08_AD", positive: true },
    { f1: "07_AE", f2: "08_AE", positive: true },
    { f1: "07_AF", f2: "08_AG", positive: false },
    { f1: "07_AG", f2: "08_AH", positive: false },
    { f1: "07_AH", f2: "08_AI", positive: false },
    { f1: "07_AI", f2: "08_AJ", positive: false },
    { f1: "07_AJ", f2: "08_AF", positive: false },
    { f1: "07_AF", f2: "08_AH", positive: false },
    { f1: "07_AG", f2: "08_AI", positive: false },
    { f1: "07_AH", f2: "08_AJ", positive: false },
    { f1: "07_AI", f2: "08_AF", positive: false },
    { f1: "07_AJ", f2: "08_AG", positive: false },
    { f1: "07_AF", f2: "08_AI", positive: false },
    { f1: "07_AG", f2: "08_AJ", positive: false },
    { f1: "07_AH", f2: "08_AF", positive: false },
    { f1: "07_AI", f2: "08_AG", positive: false },
    { f1: "07_AJ", f2: "08_AH", positive: false },
    { f1: "07_AF", f2: "08_AJ", positive: false },
    { f1: "07_AG", f2: "08_AF", positive: false },
    { f1: "07_AH", f2: "08_AG", positive: false },
    { f1: "07_AI", f2: "08_AH", positive: false },
    { f1: "07_AJ", f2: "08_AI", positive: false },
    { f1: "08_AA", f2: "09_AA", positive: true },
    { f1: "08_AB", f2: "09_AB", positive: true },
    { f1: "08_AC", f2: "09_AC", positive: true },
    { f1: "08_AD", f2: "09_AD", positive: true },
    { f1: "08_AE", f2: "09_AE", positive: true },
    { f1: "08_AF", f2: "09_AG", positive: false },
    { f1: "08_AG", f2: "09_AH", positive: false },
    { f1: "08_AH", f2: "09_AI", positive: false },
    { f1: "08_AI", f2: "09_AJ", positive: false },
    { f1: "08_AJ", f2: "09_AF", positive: false },
    { f1: "08_AF", f2: "09_AH", positive: false },
    { f1: "08_AG", f2: "09_AI", positive: false },
    { f1: "08_AH", f2: "09_AJ", positive: false },
    { f1: "08_AI", f2: "09_AF", positive: false },
    { f1: "08_AJ", f2: "09_AG", positive: false },
    { f1: "08_AF", f2: "09_AI", positive: false },
    { f1: "08_AG", f2: "09_AJ", positive: false },
    { f1: "08_AH", f2: "09_AF", positive: false },
    { f1: "08_AI", f2: "09_AG", positive: false },
    { f1: "08_AJ", f2: "09_AH", positive: false },
    { f1: "08_AF", f2: "09_AJ", positive: false },
    { f1: "08_AG", f2: "09_AF", positive: false },
    { f1: "08_AH", f2: "09_AG", positive: false },
    { f1: "08_AI", f2: "09_AH", positive: false },
    { f1: "08_AJ", f2: "09_AI", positive: false }
];
export const LARGE_PUZZLE = {
    featNames: LARGE_FEAT_NAMES,
    feats: LARGE_FEATS,
    consts: LARGE_CONSTRAINTS
};
export const featureNames = () => ["Sport", "Pet", "Nationality", "Drink", "Name", "Vehicle", "Job", "Instrument", "Fruit", "City"];
export const features = () => new Map([
    ["Sport", ["Volleyball", "Cricket", "Basketball", "Football", "Baseball", "Hockey", "Boxing", "Handball", "Tennis", "Badminton"]],
    ["Pet", ["Rabbit", "Hamster", "Lion", "Cat", "Dog", "Tiger", "Snail", "Frog", "Tortoise", "Fox"]],
    ["Nationality", ["Russian", "Turkish", "German", "American", "Italian", "French", "Greek", "English", "Spanish", "Hungarian"]],
    ["Drink", ["Beer", "Cola", "Coffee", "Tea", "Milk", "Water", "Lemonade", "Juice", "Wine", "Whisky"]],
    ["Name", ["David", "Onur", "Giovanni", "Riley", "Fred", "Jack", "Laura", "Steve", "Moses", "William"]],
    ["Vehicle", ["Car", "Truck", "Bicycle", "Train", "Zeppelin", "Helicopter", "Airplane", "Jetpack", "Boat", "Submarine"]],
    ["Job", ["Programmer", "Lawyer", "Doctor", "CEO", "Nurse", "Painter", "DJ", "Historian", "Conductor", "Comedian"]],
    ["Instrument", ["Guitar", "Trumpet", "Keyboard", "Drums", "Bass", "Harmonica", "Violoncello", "Trombone", "Saxophone", "Piano"]],
    ["Fruit", ["Watermelon", "Apple", "Banana", "Dragonfruit", "Pear", "Strawberry", "Peach", "Ananas", "Apricot", "Raspberry"]],
    ["City", ["Istanbul", "Berlin", "Paris", "London", "Rome", "Moscow", "Monaco", "Tokyo", "New York", "Budapest"]],
]);
